<template>
  <div class="flex flex-col flex-1 overflow-hidden bg-white border border-gray-300 rounded">
    <div class="relative flex-1 overflow-y-scroll">
      <p-loader v-if="isLoading" />
      <table
        v-else
        class="data-table"
      >
        <thead>
          <tr>
            <th
              class="w-24"
              v-html="$tk('Reports.Booking')"
            ></th>
            <th
              class="w-24"
              v-html="$tk('Reports.Sent')"
            ></th>
            <th
              class="w-24 whitespace-no-wrap"
              v-html="$tk('Reports.DeliveryDateShort')"
            ></th>
            <th
              class="w-14"
              v-html="$tk('Reports.Type')"
            ></th>
            <th v-html="$tk('Reports.Status')"></th>
            <th v-html="$tk('Reports.From')"></th>
            <th v-html="$tk('Reports.To')"></th>
            <th
              class="w-72"
              v-html="$tk('Reports.Product')"
            ></th>
            <th
              class="whitespace-no-wrap w-14"
              title="Produkt type / product type"
              v-html="$tk('Reports.ProductTypeShort')"
            ></th>
            <th
              class="right w-14"
              title="Antall / Amount"
              v-html="$tk('Reports.Qty')"
            ></th>
            <th
              class="right w-14"
              title="Sortert / Sorted"
              v-html="$tk('Reports.Sort')"
            ></th>
            <th
              class="right w-14"
              title="Reparert / Repaired"
              v-html="$tk('Reports.Rep')"
            ></th>
            <th
              class="w-8"
              v-html="$tk('Reports.Currency')"
            ></th>
            <th
              class="right w-14"
              v-html="$tk('Reports.Amount')"
            ></th>
            <th
              class="right w-14"
              v-html="$tk('Reports.Shipping')"
            ></th>
            <th
              class="right w-14"
              v-html="$tk('Reports.Total')"
            ></th>
            <th
              class="whitespace-no-wrap"
              title="Avsenders ref / senders ref"
              v-html="$tk('Reports.SendersRef')"
            ></th>
            <th
              class="whitespace-no-wrap"
              title="Mottakers ref / recievers ref"
              v-html="$tk('Reports.RecieversRef')"
            ></th>
            <th>{{ ' ' }}</th>
          </tr>
        </thead>
        <transaction
          v-for="(order, index) in orders"
          :key="`transaction--${index}`"
          :order="order"
          :productFilter="product"
        />
      </table>
      <portal to="header">
        <div class="ml-1 text-xl font-medium text-gray-900">
          <PTranslation k="Reports.Transactions" />
        </div>
      </portal>
      <portal to="filters">
        <div class="flex space-x-2">
          <p-select
            v-model="selectedLocation"
            :items="locations"
            :placeholder="$t('Reports.AllLocations')"
            class="w-48"
            itemText="name"
            itemValue="id"
          />
          <p-select
            v-model="orderType"
            :items="orderTypes"
            :placeholder="$t('Reports.AllOrderTypes')"
            class="w-40"
            itemText="text"
            itemValue="value"
          />
          <p-select
            v-model="product"
            :items="products"
            :placeholder="$t('Reports.AllProducts')"
            class="w-44"
            itemText="name"
            itemValue="id"
          />
          <p-date-picker
            v-model="dateLower"
            :allOpen="true"
            :clearable="true"
            :placeholder="$t('Common.General.FromDate')"
            class="w-36"
          />
          <p-date-picker
            v-model="dateUpper"
            :allOpen="true"
            :clearable="true"
            :placeholder="$t('Common.General.ToDate')"
            :rightAlign="true"
            class="w-36"
          />
        </div>
      </portal>
      <portal to="actions">
        <div class="flex ml-2 space-x-2">
          <PButton
            :loading="isDownloading"
            color="secondary"
            icon="file-excel"
            @click="onDownload"
          >
            Excel
          </PButton>
        </div>
      </portal>
    </div>
  </div>
</template>

<script>
import { format, sub, startOfMonth, lastDayOfMonth } from "date-fns"
import { map, orderBy, filter } from "lodash"
import { mapGetters } from 'vuex'
import fileDownload from 'js-file-download'
import http from "@/utilities/http"
import Transaction from "../components/Transaction"

export default {
  name: "Transactions",
  data() {
    return {
      dateUpper: format(lastDayOfMonth(new Date()), "yyyy-MM-dd"),
      dateLower: format(startOfMonth(sub(new Date(), { months: 2 })), "yyyy-MM-dd"),
      selectedLocation: -1,
      locationPicker: false,
      locations: [],
      fees: true,
      sort: true,
      rep: true,
      isLoading: true,
      isDownloading: false,
      orders: [],
      orderType: "TO,HE,OO,GL",
      orderTypes: [],
      products: [],
      product: '-1',
      productTypes: [
        { text: this.$tk('Reports.AllProductTypes', true), value: 0 },
        { text: this.$tk('Reports.PurchaseAndSale', true), value: 1 },
        { text: this.$tk('Reports.Commission', true), value: 2 },
        { text: this.$tk('Reports.Pooling', true), value: 3 }
      ]
    }
  },
  components: {
    Transaction
  },
  computed: {
    ...mapGetters(['location', 'user']),
    queryState() {
      return `${this.dateLower}-${this.dateUpper}-${this.orderType}-${this.selectedLocation}`
    },
    params() {
      const obj = {
        isReport: true,
        sendLower: this.dateLower,
        sendUpper: this.dateUpper,
        transactiontype: this.orderType,
        withFees: this.fees,
        withQualityChanges: true,
        withSort: this.sort,
      }
      if (this.selectedLocation !== -1) {
        obj.locationId = this.selectedLocation
      } else if (this.locationPicker) {
        obj.customerId = this.user.customerId
      } else {
        obj.locationId = this.location.id
      }
      if (this.productType) {
        obj.productType = this.productType
      }
      return obj
    }
  },
  watch: {
    async queryState() {
      try {
        this.isLoading = true
        this.orders = await this.loadOrders()
      } finally {
        this.isLoading = false
      }
    }
  },
  methods: {
    async loadOrders() {
      const orders = await http.get("orders", { params: this.params })
      return orderBy(orders, "id", "asc")
    },
    async loadLocations() {
      const locations = await http.get('locations', { params: { customerId: this.user.customerId } })
      locations.unshift({ name: this.$tk('Reports.AllLocations', true), id: -1 })
      return locations
    },
    async loadProducts() {
      const products = await http.get('products', { params: { all: true } })
      products.unshift({ name: this.$tk('Reports.AllProducts', true), id: '-1' })
      return products
    },
    async onDownload() {
      const params = {
        ...this.params,
        excel: "download"
      }
      this.isDownloading = true
      const data = await http.get("orders", { params: params, responseType: "blob" })
      fileDownload(data, `${format(new Date(), "yyyy-MM-dd")}_transactions.xlsx`)
      this.isDownloading = false
    }
  },
  async created() {
    try {
      this.rep = this.location.isForcedRep === 1
      this.locationPicker = this.user.reportLevel === 1

      if (this.locationPicker) {
        this.locations = await this.loadLocations()
      }
      this.products = await this.loadProducts()
      this.orderTypes = filter(
        map(await http.get('TransactionTypes'), r => {
          return { value: r.internalCode, text: r.externalName }
        }),
        r => {
          return this.orderType.includes(r.value)
        }
      )
      this.orderTypes.unshift({ value: map(this.orderTypes, r => r.value).join(','), text: this.$tk('Reports.AllOrderTypes', true) })
      this.orders = await this.loadOrders()
    } catch (e) {
      console.error("error creating transaction", e)
    } finally {
      this.isLoading = false
    }
  },
}
</script>

<style scoped>
thead>tr>th {
  @apply p-2 sticky;
}
</style>
